
import { Component } from 'vue-property-decorator';
import * as echarts from 'echarts/core';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import { addComma } from '@/util/number';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		StackedBarChartFilterable,
		TableCollapseToggle,
	},
})
export default class BpCashFlowBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'year'
	> = ['year'];

	public eachMonthTotal: any[] = [];
	public accumulatedTotal: any[] = [];
	public xAxisData: string[] = [];
	public periodTotalData: any[] = [];
	public detailFields: any[] = [];
	public series: echarts.EChartsCoreOption[] | any = [];

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.cashFlow;
	}

	public get dateFormatted() {
		return [
            moment(this.selectedAsOfDate)
                .add(-11, 'months')
                .startOf('day')
                .valueOf(),
            this.selectedAsOfDate,
        ];
	}

	public get expensiveHook() {
		const { selectedAsOfDate, selectedDateRange } = this;
		return JSON.stringify([selectedAsOfDate, selectedDateRange]);
	}

	public async expensiveCalc() {
		const table = getTableName('transactions');
		const monthlyStart = moment(this.selectedAsOfDate)
			.add(-11, 'months')
			.startOf('day')
			.valueOf();
		const monthlyEnd = this.selectedAsOfDate;

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['transactions']);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.3);

		const cashBankFilter = `AND "ACCTYPE" = 'CA' AND "SPECIALACCTYPE" IN ('CH', 'BA')`;

		const [monthlyResult, asOfResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT
					EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST("DOCDATE" AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as year,
					EXTRACT(MONTH FROM EPOCH_MS(TRY_CAST("DOCDATE" AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as month,
					SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${monthlyStart} AND ${monthlyEnd}
					${cashBankFilter}
				GROUP BY year, month
				ORDER BY year, month
			`),
			queryHead.rawQuery(`
				SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${monthlyEnd}
					${cashBankFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		// Build monthly data array with moment iteration (replicates _loadDimensionByPeriod)
		const startMoment = moment(monthlyStart).startOf('month');
		const endMoment = moment(monthlyEnd).endOf('month');
		const monthlyMap: Record<string, number> = {};
		for (const row of monthlyResult) {
			monthlyMap[`${Number(row.year)}-${Number(row.month)}`] = Number(row.total) || 0;
		}

		const monthlyCashBank: Array<{ text: string; sum: number }> = [];
		const m = startMoment.clone();
		while (m.isSameOrBefore(endMoment, 'month')) {
			const key = `${m.year()}-${m.month() + 1}`;
			monthlyCashBank.push({
				text: m.format('MMM YYYY'),
				sum: monthlyMap[key] || 0,
			});
			m.add(1, 'month');
		}

		const cloneCashBankEachMonth = monthlyCashBank.map((dd) => dd.sum);

		const currentMonth = moment().endOf('month');
		const isThisYear = this.selectedAsOfDate > currentMonth.valueOf();
		const currentMonthIndex = isThisYear ? currentMonth.month() + 1 : 12;

		let asOfCashBank = Number(asOfResult[0] && asOfResult[0].total) || 0;

		const eachMonthResults: Array<{ value: number; itemStyle: any }> = [];
		const accumulatedResults: number[] = [];

		for (let i = 0; i <= monthlyCashBank.length; i++) {
			const object = { value: 0, itemStyle: { color: '' } };
			if (i >= currentMonthIndex) {
				continue;
			}

			asOfCashBank += cloneCashBankEachMonth[i];
			object.value = cloneCashBankEachMonth[i];
			object.itemStyle.color =
				object.value > 0 ? GobiColor.COLOR1 : GobiColor.COLOR3;

			eachMonthResults[i] = object;
			accumulatedResults[i] = asOfCashBank;
		}

		this.accumulatedTotal = accumulatedResults;
		this.eachMonthTotal = eachMonthResults;

		const totalNetCashFlow = monthlyCashBank
			.map((dd) => dd.sum)
			.reduce((a, b) => a + b, 0);

		this.periodTotalData = [
			{
				net_cash_flow:
					this.currencySymbol +
					' ' +
					addComma(totalNetCashFlow, gdbx.numDecimal, true),
			},
		];

		this.detailFields = [
			{
				key: 'net_cash_flow',
				label: 'Net Cash Flow',
				textAlign: 'center',
			},
		];

		const netCashFlow: echarts.EChartsCoreOption | any = {
			name: 'Net Cash Flow',
			type: 'bar',
			data: this.eachMonthTotal,
		};

		const cashLevel: echarts.EChartsCoreOption | any = {
			name: 'Cash Level',
			type: 'line',
			data: this.accumulatedTotal,
			itemStyle: {
				color: GobiColor.COLOR7,
			},
		};

		this.xAxisData = monthlyCashBank.map((dd) => dd.text);

		this.series = [cashLevel, netCashFlow];

		this.saveHistory(
			'series',
			'xAxisData',
			'eachMonthTotal',
			'accumulatedTotal',
		);
	}
}
